import { isBefore, startOfDay } from 'date-fns';
import { EStopType, IStopDetailedForShipper } from '../stop';

import {
  EStopCorrectionTypeForShipper,
  EValidationErrorTypeForShipper,
  IValidationResultForShipper,
} from './freight-shipper-validation.types';

export function validateAndCorrectStopsForShipper(stops: IStopDetailedForShipper[]): {
  result: IValidationResultForShipper;
  correctedStops: IStopDetailedForShipper[];
} {
  const result: IValidationResultForShipper = {
    hasErrors: false,
    corrections: [],
    errors: [],
  };

  // Always return an inmutable (or a shallow copy)
  const correctedStops = stops.map((stop) => ({ ...stop }));

  // If there are not enough stops yet, do not perform validations
  if (correctedStops.length < 2) {
    return { result, correctedStops };
  }

  // First stop always has type LOAD
  // Last stop always has type UNLOAD
  enforceFirstLastStopTypes(correctedStops, result);
  balanceLoadUnloadWeights(correctedStops, result);

  // TODO validate by stop... refactor this
  for (const stop of correctedStops) {
    const stopValidationResult = validateStop(stop);
    if (stopValidationResult.hasErrors) {
      result.errors.push(...stopValidationResult.errors);
      result.hasErrors = true;
    }
  }

  // validate that stop dates are in chronological order (only date, not time)
  validateChronologicalOrder(correctedStops, result);

  return { result, correctedStops };
}

function enforceFirstLastStopTypes(stops: IStopDetailedForShipper[], result: IValidationResultForShipper): void {
  const firstStop = stops[0];
  const lastStop = stops[stops.length - 1];

  if (firstStop.type !== EStopType.LOAD) {
    const oldType = firstStop.type;
    stops[0] = { ...firstStop, type: EStopType.LOAD };
    result.corrections.push({
      stopId: firstStop.id,
      correctionType: EStopCorrectionTypeForShipper.STOP_UPDATED_TYPE,
      oldValue: oldType,
      newValue: EStopType.LOAD,
    });
  }

  if (lastStop.type !== EStopType.UNLOAD) {
    const oldType = lastStop.type;
    stops[stops.length - 1] = { ...lastStop, type: EStopType.UNLOAD };
    result.corrections.push({
      stopId: lastStop.id,
      correctionType: EStopCorrectionTypeForShipper.STOP_UPDATED_TYPE,
      oldValue: oldType,
      newValue: EStopType.UNLOAD,
    });
  }
}

function balanceLoadUnloadWeights(stops: IStopDetailedForShipper[], result: IValidationResultForShipper): void {
  let totalNetWeight = 0;
  let totalPackages = 0;
  let totalUnloadedNetWeight = 0;
  let totalUnloadedPackages = 0;

  const lastStopIndex = stops.length - 1;
  const lastStop = stops[lastStopIndex];

  for (const stop of stops) {
    const type = stop.type;

    if (type === EStopType.LOAD || type === EStopType.UNLOAD_LOAD) {
      totalNetWeight += Number(stop.netWeight) || 0;
      totalPackages += Number(stop.packages) || 0;
    }

    if (type === EStopType.UNLOAD || type === EStopType.UNLOAD_LOAD) {
      totalUnloadedNetWeight += Number(stop.unloadedNetWeight) || 0;
      totalUnloadedPackages += Number(stop.unloadedPackages) || 0;
    }
  }

  const netWeightDiff = totalNetWeight - totalUnloadedNetWeight;
  const packagesDiff = totalPackages - totalUnloadedPackages;

  if (netWeightDiff !== 0) {
    const updated = {
      ...lastStop,
      unloadedNetWeight: (lastStop.unloadedNetWeight || 0) + netWeightDiff,
    };
    stops[lastStopIndex] = updated;

    result.corrections.push({
      stopId: lastStop.id,
      correctionType: EStopCorrectionTypeForShipper.STOP_UPDATED_UNLOADED_WEIGHT,
      oldValue: lastStop.unloadedNetWeight,
      newValue: updated.unloadedNetWeight,
    });
  }

  if (packagesDiff !== 0) {
    const updated = {
      ...stops[lastStopIndex],
      unloadedPackages: (lastStop.unloadedPackages || 0) + packagesDiff,
    };
    stops[lastStopIndex] = updated;

    result.corrections.push({
      stopId: lastStop.id,
      correctionType: EStopCorrectionTypeForShipper.STOP_UPDATED_UNLOADED_PACKAGES,
      oldValue: lastStop.unloadedPackages,
      newValue: updated.unloadedPackages,
    });
  }

  const finalUnloadedNetWeight = stops[lastStopIndex].unloadedNetWeight || 0;
  const finalUnloadedPackages = stops[lastStopIndex].unloadedPackages || 0;

  if (finalUnloadedNetWeight <= 0 || finalUnloadedPackages <= 0) {
    result.errors.push({
      stopId: lastStop.id,
      errorType: EValidationErrorTypeForShipper.STOPS_INVALID_UNLOADED_VALUES,
    });
    result.hasErrors = true;
  }
}

function validateChronologicalOrder(stops: IStopDetailedForShipper[], result: IValidationResultForShipper): void {
  for (let i = 1; i < stops.length; i++) {
    const prev = stops[i - 1];
    const curr = stops[i];

    if (!prev.until || !curr.until) continue;

    const prevDate = startOfDay(new Date(prev.until));
    const currDate = startOfDay(new Date(curr.until));

    if (isBefore(currDate, prevDate)) {
      result.errors.push({
        stopId: curr.id,
        errorType: EValidationErrorTypeForShipper.STOPS_INVALID_DATES,
      });
      result.hasErrors = true;
    }
  }
}

function validateStop(stop: IStopDetailedForShipper): IValidationResultForShipper {
  const result: IValidationResultForShipper = {
    hasErrors: false,
    corrections: [],
    errors: [],
  };

  validateStopAddress(stop, result);

  validateStopByType(stop, result);

  validateStopDates(stop, result);

  return result;
}

function validateStopAddress(stop: IStopDetailedForShipper, result: IValidationResultForShipper): void {
  if (!stop.address || !stop.address.postalCode) {
    result.errors.push({
      stopId: stop.id,
      errorType: EValidationErrorTypeForShipper.STOP_WITHOUT_ADDRESS,
    });
    result.hasErrors = true;
  }
}

function validateStopByType(stop: IStopDetailedForShipper, result: IValidationResultForShipper): void {
  if (stop.type === EStopType.LOAD || stop.type === EStopType.UNLOAD_LOAD) {
    validateRequiredField(stop, 'netWeight', EValidationErrorTypeForShipper.STOP_MISSING_WEIGHT, 'weight', result);
    validateRequiredField(stop, 'packages', EValidationErrorTypeForShipper.STOP_MISSING_PACKAGES, 'packages', result);
    validateMinLength(
      stop,
      'goodsDescription',
      3,
      EValidationErrorTypeForShipper.STOP_INVALID_GOODS_DESCRIPTION,
      'nature',
      result,
    );
  }

  if (stop.type === EStopType.UNLOAD || stop.type === EStopType.UNLOAD_LOAD) {
    validateRequiredField(
      stop,
      'unloadedNetWeight',
      EValidationErrorTypeForShipper.STOP_MISSING_UNLOADED_WEIGHT,
      'unloadedWeight',
      result,
    );
    validateRequiredField(
      stop,
      'unloadedPackages',
      EValidationErrorTypeForShipper.STOP_MISSING_UNLOADED_PACKAGES,
      'unloadedPackages',
      result,
    );
  }
}

function validateStopDates(stop: IStopDetailedForShipper, result: IValidationResultForShipper): void {
  validateRequiredField(stop, 'until', EValidationErrorTypeForShipper.STOP_MISSING_UNTIL_DATE, 'until', result);
  validateRequiredField(stop, 'since', EValidationErrorTypeForShipper.STOP_MISSING_SINCE_DATE, 'until', result);

  // TODO review, it is a draft
  if (stop.since && new Date(stop.since) < new Date()) {
    result.errors.push({
      stopId: stop.id,
      errorType: EValidationErrorTypeForShipper.STOP_SINCE_BEFORE_NOW,
      field: 'since',
    });
    result.hasErrors = true;
  }

  // TODO review, it is a draft
  if (stop.since && stop.until && new Date(stop.until) < new Date(stop.since)) {
    result.errors.push({
      stopId: stop.id,
      errorType: EValidationErrorTypeForShipper.STOP_UNTIL_BEFORE_SINCE,
      field: 'until',
    });
    result.hasErrors = true;
  }
}

function validateRequiredField(
  stop: IStopDetailedForShipper,
  fieldName: keyof IStopDetailedForShipper,
  errorType: EValidationErrorTypeForShipper,
  fieldLabel: string,
  result: IValidationResultForShipper,
): void {
  if (!stop[fieldName]) {
    result.errors.push({
      stopId: stop.id,
      errorType: errorType,
      field: fieldLabel,
    });
    result.hasErrors = true;
  }
}

function validateMinLength(
  stop: IStopDetailedForShipper,
  fieldName: keyof IStopDetailedForShipper,
  minLength: number,
  errorType: EValidationErrorTypeForShipper,
  fieldLabel: string,
  result: IValidationResultForShipper,
): void {
  const value = stop[fieldName] as string | undefined;
  if (!value || value.length < minLength) {
    result.errors.push({
      stopId: stop.id,
      errorType: errorType,
      field: fieldLabel,
    });
    result.hasErrors = true;
  }
}
